import React, { useState, useEffect } from 'react';
import { groupAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useGroup } from '../contexts/GroupContext';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ErrorMessage from '../components/Common/ErrorMessage';
import SuccessMessage from '../components/Common/SuccessMessage';

const GroupManagement = () => {
  const { isSuperAdmin, customerId } = useAuth();
  const { groups, loadGroups, selectedGroupId, selectGroup } = useGroup();
  
  // All hooks must be called before any conditional returns
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    customer_id: customerId || '',
  });

  useEffect(() => {
    if (isSuperAdmin) {
      loadGroups();
    }
  }, [isSuperAdmin, loadGroups]);

  // Only super admins can access this page - check after hooks
  if (!isSuperAdmin) {
    return (
      <div className="page-container">
        <div className="card">
          <div className="card-body">
            <ErrorMessage message="Access denied. Only super administrators can access the Groups management page." />
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (editingGroup) {
        await groupAPI.update(editingGroup.id, formData);
        setSuccess('Group updated successfully');
      } else {
        await groupAPI.create(formData);
        setSuccess('Group created successfully');
      }

      await loadGroups();
      setShowForm(false);
      setEditingGroup(null);
      setFormData({ name: '', code: '', description: '', customer_id: customerId || '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save group');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      code: group.code,
      description: group.description || '',
      customer_id: group.customer_id,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this group? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await groupAPI.delete(id);
      setSuccess('Group deleted successfully');
      await loadGroups();
      if (selectedGroupId === id) {
        selectGroup(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingGroup(null);
    setFormData({ name: '', code: '', description: '', customer_id: customerId || '' });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Group Management</h2>
        <button className="btn btn-primary" onClick={() => {
          setEditingGroup(null);
          setFormData({ name: '', code: '', description: '', customer_id: customerId || '' });
          setShowForm(true);
        }}>
        <i class="fa-solid fa-plus"></i> Create Group
        </button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError(null)} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess(null)} />}

      {/* Create/Edit Group Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCancel();
          }
        }}>
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{editingGroup ? 'Edit Group' : 'Create New Group'}</h3>
              <button 
                className="btn-close" 
                onClick={handleCancel}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
              <div className="form-group">
                <label>Group Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Group Code *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                  disabled={!!editingGroup}
                />
                <small className="form-text text-muted">Unique code for this group (cannot be changed after creation)</small>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              {isSuperAdmin && (
                <div className="form-group">
                  <label>Customer ID *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: parseInt(e.target.value) })}
                    required
                  />
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingGroup ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>Groups</h3>
        </div>
        <div className="card-body">
          {groups.length === 0 ? (
            <p>No groups found. Create your first group to get started.</p>
          ) : (
            <div className="table-container">
              <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id} className={selectedGroupId === group.id ? 'table-active' : ''}>
                    <td>{group.name}</td>
                    <td><code>{group.code}</code></td>
                    <td>{group.customer?.name || 'N/A'}</td>
                    <td>
                      <span className={`badge ${group.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {group.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => selectGroup(group.id)}
                          title="Select this group"
                        >
                          {selectedGroupId === group.id ? '✓ Selected' : 'Select'}
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleEdit(group)}
                        >
                           <i class="fa-solid fa-pen-to-square"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(group.id)}
                        >
                          <i class="fa-solid fa-trash-can"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupManagement;

